import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useTranslation } from '../hooks/useTranslation';
import ProtectedComponent from './ProtectedComponent';
import { 
  ChevronRight, 
  ChevronLeft, 
  Home, 
  History, 
  Settings,
  FileText,
  Shield,
  Briefcase,
  Lock
} from 'lucide-react';

const Sidebar = ({ activePage, onNavigate, onToggleCollapse, collapsed = false }) => {
  const { currentUser, currentOrg, userRole, isOrgAdmin, isGlobalAdmin, subscription, trialInfo } = useAuth();
  const t = useTranslation();
  const [isCollapsed, setIsCollapsed] = useState(collapsed);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const sidebarRef = useRef(null);

  // Set up CSS header position tracking - this runs once on mount
  useEffect(() => {
    // Add CSS to the document for direct header tracking
    const styleElement = document.createElement('style');
    styleElement.innerHTML = `
      .sidebar-container {
        position: fixed;
        left: 0;
        top: var(--header-height, 72px);
        height: calc(100vh - var(--header-height, 72px));
        z-index: 40;
        transition: transform 0.3s ease-in-out, width 0.3s ease-in-out;
        will-change: transform, width, top, height;
      }
    `;
    document.head.appendChild(styleElement);

    // Set up the header height observer
    const updateHeaderPosition = () => {
      const headerElement = 
        document.querySelector('header') || 
        document.querySelector('.header') || 
        document.querySelector('.app-header') || 
        document.querySelector('[data-header]');
      
      if (headerElement) {
        // Directly update the CSS variable at the document level for immediate effect
        document.documentElement.style.setProperty('--header-height', `${headerElement.offsetHeight}px`);
      }
    };
    
    // Update on load
    updateHeaderPosition();
    
    // Set up mutation observer to detect DOM changes that might affect header
    const mutationObserver = new MutationObserver(updateHeaderPosition);
    mutationObserver.observe(document.body, { 
      childList: true,
      subtree: true,
      attributes: true
    });

    // Also set up resize observer for more immediate size change detection
    const resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(updateHeaderPosition);
    });
    
    const header = 
      document.querySelector('header') || 
      document.querySelector('.header') || 
      document.querySelector('.app-header') || 
      document.querySelector('[data-header]');
      
    if (header) {
      resizeObserver.observe(header);
    }
    
    // Listen for scroll events which might trigger header size changes
    window.addEventListener('scroll', () => {
      requestAnimationFrame(updateHeaderPosition);
    }, { passive: true });
    
    // Listen for window resize events
    window.addEventListener('resize', () => {
      requestAnimationFrame(updateHeaderPosition);
    });
    
    // Clean up
    return () => {
      if (styleElement && styleElement.parentNode) {
        styleElement.parentNode.removeChild(styleElement);
      }
      if (header && resizeObserver) {
        resizeObserver.unobserve(header);
      }
      mutationObserver.disconnect();
      window.removeEventListener('scroll', updateHeaderPosition);
      window.removeEventListener('resize', updateHeaderPosition);
    };
  }, []); // Run once on mount

  // Effect to check if we're on mobile
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };
    
    window.addEventListener('resize', handleResize);
    handleResize(); // Initial check
    
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Effect to sync with parent component's collapsed state
  useEffect(() => {
    setIsCollapsed(collapsed);
  }, [collapsed]);

  // Toggle sidebar collapsed state
  const toggleSidebar = () => {
    const newCollapsedState = !isCollapsed;
    setIsCollapsed(newCollapsedState);
    if (onToggleCollapse) {
      onToggleCollapse(newCollapsedState);
    }
  };

  // Close sidebar when clicking overlay (on mobile)
  const handleOverlayClick = () => {
    if (isMobile && !isCollapsed) {
      toggleSidebar();
    }
  };

  // Check if user has professional features (trial is treated as professional)
  const hasProfessionalFeatures = 
    (subscription?.productName === 'Professional' && subscription?.status === 'active') ||
    (subscription?.status === 'trialing') ||
    trialInfo?.isInTrial;
  
  // Navigation items (restored original order)
  const navigationItems = [
    { id: 'home', icon: <Home size={18} />, label: t('sidebar.home'), onClick: () => onNavigate('home'), path: '/' },
    { id: 'monitoring', icon: <Shield size={18} />, label: t('sidebar.monitoring'), onClick: () => onNavigate('monitoring'), path: '/monitoring' },
    { id: 'cases', icon: <Briefcase size={18} />, label: t('sidebar.cases'), onClick: () => onNavigate('cases'), path: '/cases' },
    { 
      id: 'massUpload', 
      icon: <FileText size={18} />, 
      label: t('sidebar.massUpload'), 
      onClick: () => onNavigate('massUpload'), 
      path: '/massUpload',
      isPremium: true,
      hasAccess: hasProfessionalFeatures
    },
    { id: 'history', icon: <History size={18} />, label: t('sidebar.searchHistory'), onClick: () => onNavigate('history'), path: '/history' }
  ];

  // Admin navigation items
  const adminItems = [];
  
  // Organization admin items
  if (isOrgAdmin) {
    adminItems.push({
      id: 'org-admin',
      icon: <Settings size={18} />,
      label: 'Organization Admin',
      onClick: () => onNavigate('org-admin'),
      path: '/org-admin',
      requiredRole: 'org-admin'
    });
  }
  
  // Global admin items  
  if (isGlobalAdmin) {
    adminItems.push({
      id: 'super-admin',
      icon: <Settings size={18} />,
      label: 'Super Admin',
      onClick: () => onNavigate('super-admin'),
      path: '/super-admin',
      requiredRole: 'global-admin'
    });
  }

  const allItems = [...navigationItems, ...adminItems];

  // Base classes
  const sidebarBaseClasses = `sidebar-container bg-white dark:bg-slate-800 border-r border-slate-200 dark:border-slate-700`;
  
  const sidebarCollapsedClasses = "w-16";
  const sidebarExpandedClasses = "w-64"; // Standard width for expanded sidebar

  // Mobile specific classes
  const mobileBaseTransform = "-translate-x-full"; // Hidden off-screen by default on mobile
  const mobileExpandedTransform = "translate-x-0"; // Slides in from left when open on mobile

  // Overlay classes
  const overlayBaseClasses = "fixed inset-0 z-30 bg-black/50 transition-opacity duration-300 ease-in-out md:hidden"; // Hidden on medium screens and up
  const overlayVisibleClasses = "opacity-100 pointer-events-auto";
  const overlayHiddenClasses = "opacity-0 pointer-events-none";

  return (
    <>
      {/* Overlay for mobile view */}
      <div 
        className={`
          ${overlayBaseClasses} 
          ${!isCollapsed && isMobile ? overlayVisibleClasses : overlayHiddenClasses}
        `}
        onClick={handleOverlayClick}
      />
      
      {/* Sidebar container */}
      <div 
        ref={sidebarRef}
        className={`
          ${sidebarBaseClasses} 
          ${isCollapsed ? sidebarCollapsedClasses : sidebarExpandedClasses}
          ${isMobile ? (isCollapsed ? mobileBaseTransform : mobileExpandedTransform) : ''} 
          flex flex-col
        `}
      >
        {/* Sidebar Header */}
        <div className={`flex items-center ${isCollapsed ? 'justify-center' : 'justify-between'} p-4 border-b border-slate-200 dark:border-slate-700 h-[60px] flex-shrink-0`}>
          {!isCollapsed && (
            <div className="text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
              {t('sidebar.navigation')}
            </div>
          )}
          <button 
            className="flex h-7 w-7 items-center justify-center rounded-md border border-slate-200 bg-slate-50 text-slate-500 hover:bg-slate-100 hover:text-slate-700 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-400 dark:hover:bg-slate-600 dark:hover:text-slate-200" 
            onClick={toggleSidebar}
          >
            {isCollapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
          </button>
        </div>

        {/* Sidebar Content (Navigation) */}
        <div className="flex-1 overflow-y-hidden py-2 min-h-0">
          <nav className="w-full h-full">
            <ul className="list-none p-0 m-0">
              {allItems.map(item => {
                const isActive = activePage === item.id;
                
                // Check if this is a premium feature without access (define this first!)
                const isLockedPremium = item.isPremium && !item.hasAccess;
                
                // Base classes for nav item
                const navItemBase = "flex items-center mx-3 my-0.5 rounded-md px-3 py-2 cursor-pointer transition-colors duration-200 ease-in-out relative";
                
                // Classes for collapsed state
                const navItemCollapsed = isCollapsed ? "justify-center px-0" : ""; 
                
                // Classes for active state
                const navItemActive = isActive 
                  ? "bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-900 dark:to-indigo-900 text-white dark:text-blue-200 font-medium" 
                  : isLockedPremium
                    ? "text-slate-600 dark:text-slate-300" // No hover effects for locked items
                    : "text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"; 

                // Classes for icons
                const iconBase = `flex-shrink-0 h-5 w-5 min-w-[20px] items-center justify-center ${isCollapsed ? 'mx-auto' : 'mr-3'}`;
                const iconColor = isActive ? 'text-blue-400' : 'text-slate-400';
                
                const navItem = (
                  <li 
                    key={item.id} 
                    className={`${navItemBase} ${navItemCollapsed} ${navItemActive} ${isLockedPremium ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                    onClick={isLockedPremium ? undefined : item.onClick}
                    title={isCollapsed ? (isLockedPremium ? `${item.label} (Premium)` : item.label) : undefined}
                  >
                    <div className={`${iconBase} ${iconColor}`}>
                      {item.icon}
                    </div>
                                          {!isCollapsed && (
                        <div className="text-sm flex-1 truncate flex items-center">
                          {item.label}
                          {item.isPremium && !item.hasAccess && (
                            <Lock className="ml-2 w-3 h-3 text-slate-600 dark:text-slate-300" />
                          )}
                        </div>
                      )}
                  </li>
                );

                // Wrap admin items with ProtectedComponent
                return item.requiredRole ? (
                  <ProtectedComponent key={item.id} requiredRole={item.requiredRole}>
                    {navItem}
                  </ProtectedComponent>
                ) : navItem;
              })}
            </ul>
          </nav>
        </div>

        {/* Sidebar Footer (User Info) */}
        {!isCollapsed && (
          <div className="mt-auto border-t border-slate-200 dark:border-slate-700 p-4 flex-shrink-0">
            <div className="flex items-center">
              <div className="mr-3 flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-700 dark:to-indigo-700 text-sm font-semibold text-white">
                {currentUser?.displayName 
                  ? currentUser.displayName.charAt(0).toUpperCase() 
                  : currentUser?.email?.charAt(0).toUpperCase() || '?'} 
              </div>
              <div className="flex-1 overflow-hidden">
                <div className="truncate text-sm font-medium text-slate-800 dark:text-slate-200">
                  {currentUser?.displayName || currentUser?.email}
                </div>
                <div className="text-xs text-slate-500 dark:text-slate-400">
                  {isGlobalAdmin ? 'Super Admin' : isOrgAdmin ? 'Org Admin' : 'Member'}
                  {currentOrg && (
                    <div className="truncate">{currentOrg.name}</div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default Sidebar;